import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { Alert, Button, Card, Field, Input, PageHeader, Spinner } from "@empite/ui-components";
import type { Creation } from "../marketplace-client";
import {
  useCreateCreation,
  useCreation,
  useSetPublished,
  useUploadFile,
} from "../marketplace-hooks";
import type { UploadPhase } from "../marketplace-hooks";
import { formatBytes } from "../upload";
import { ErrorAlert, RequireSession, StateBadge } from "./common";

export interface UploadSearch {
  /** An existing draft to add files to, so "Add a file" from the detail page
   *  lands in step two instead of making a second creation. */
  creation?: string;
}

/**
 * The upload wizard: describe it, send the bytes, publish it.
 *
 * Three steps because the SERVER has three, not because a form looked long:
 * a draft has to exist before a file can be granted an object key, and the
 * bytes have to be verified and scanned before publishing is allowed. A
 * single-screen form would have to hide those failures or invent an order of
 * its own.
 */
export function UploadWizard({ search }: { search: UploadSearch }) {
  return (
    <RequireSession>
      {() => <Wizard existing={search.creation} />}
    </RequireSession>
  );
}

function Wizard({ existing }: { existing?: string }) {
  const [draftID, setDraftID] = useState<string | null>(existing ?? null);

  return (
    <>
      <PageHeader
        title="Upload a creation"
        description="Any file, stored and served exactly as you upload it. Nothing about it is opened, converted or previewed."
      />
      {draftID === null ? (
        <DetailsStep onCreated={(creation) => setDraftID(creation.id)} />
      ) : (
        <FilesStep creationID={draftID} />
      )}
    </>
  );
}

// The client-side rules mirror internal/marketplace/service.go:Create. They
// are a courtesy, not the check: the server validates every one of them
// again, and its answer is the one that decides.
const detailsSchema = z.object({
  title: z.string().min(1, "Give it a title.").max(200, "200 characters at most."),
  summary: z.string().max(500, "500 characters at most."),
  description: z.string().max(20000, "That description is very long."),
  slug: z
    .string()
    .max(80, "80 characters at most.")
    .regex(/^$|^[a-z0-9]+(-[a-z0-9]+)*$/, "Lowercase letters, digits and single hyphens."),
});

type DetailsValues = z.infer<typeof detailsSchema>;

function DetailsStep({ onCreated }: { onCreated: (creation: Creation) => void }) {
  const create = useCreateCreation();
  const [formError, setFormError] = useState<unknown>(null);
  const form = useZodForm<DetailsValues>(detailsSchema, {
    defaultValues: { title: "", summary: "", description: "", slug: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      onCreated(await create.mutateAsync(values));
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["title", "summary", "description", "slug"])) {
        setFormError(err);
      }
    }
  });

  return (
    <Card title="1. Describe it">
      <form onSubmit={submit} noValidate>
        <ErrorAlert error={formError} title="The draft was not created" />
        <Field label="Title" htmlFor="title" error={form.formState.errors.title?.message}>
          <Input id="title" invalid={Boolean(form.formState.errors.title)} {...form.register("title")} />
        </Field>
        <Field
          label="Summary"
          htmlFor="summary"
          hint="One line, shown on the browse page."
          error={form.formState.errors.summary?.message}
        >
          <Input id="summary" {...form.register("summary")} />
        </Field>
        <Field label="Description" htmlFor="description" error={form.formState.errors.description?.message}>
          <Input id="description" {...form.register("description")} />
        </Field>
        <Field
          label="Slug"
          htmlFor="slug"
          hint="Optional. Left blank, one is derived from the title."
          error={form.formState.errors.slug?.message}
        >
          <Input id="slug" {...form.register("slug")} />
        </Field>
        <Button type="submit" busy={create.isPending}>
          Create draft
        </Button>
      </form>
    </Card>
  );
}

/** What the wizard says while each phase of the lifecycle runs. */
const PHASE_TEXT: Record<UploadPhase, string> = {
  hashing: "Checksumming the file in your browser…",
  granting: "Asking for an upload link…",
  sending: "Sending the bytes to storage…",
  completing: "Verifying size and checksum, then scanning…",
};

function FilesStep({ creationID }: { creationID: string }) {
  const navigate = useNavigate();
  const creation = useCreation(creationID);
  const upload = useUploadFile();
  const publish = useSetPublished();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<UploadPhase | null>(null);

  const files = creation.data?.files ?? [];
  const ready = files.length > 0 && files.every((f) => !f.required || f.state === "active");

  const send = () => {
    if (!file) {
      return;
    }
    upload.mutate(
      { creationID, file, onPhase: setPhase },
      {
        onSettled: () => setPhase(null),
        onSuccess: () => setFile(null),
      },
    );
  };

  return (
    <>
      <Card title="2. Add the file">
        <ErrorAlert error={upload.error} title="That upload did not finish" />
        <input
          type="file"
          aria-label="Choose a file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        {file ? (
          <p className="esm-file__meta">
            {file.name} · {formatBytes(file.size)}
          </p>
        ) : null}
        <div className="esm-actions">
          <Button busy={upload.isPending} disabled={!file} onClick={send}>
            Upload
          </Button>
        </div>
        {phase ? (
          <p className="esm-file__meta">
            <Spinner inline label={PHASE_TEXT[phase]} /> {PHASE_TEXT[phase]}
          </p>
        ) : null}
        {/* The scan verdict, not the store's 200, is what says an upload
            worked. A rejected or quarantined file stays listed here so the
            author can see what happened to it. */}
        {upload.data && upload.data.state !== "active" ? (
          <Alert tone="danger" title={`That file was ${upload.data.state}`}>
            The scanner would not clear it, so it cannot be published. Upload a different file.
          </Alert>
        ) : null}
        {creation.isPending ? (
          <Spinner label="Loading the draft" />
        ) : files.length === 0 ? (
          <p>No files on this draft yet.</p>
        ) : (
          <ul className="esm-file-list">
            {files.map((f) => (
              <li key={f.id} className="esm-file">
                <div className="esm-file__body">
                  <strong>{f.declared_filename}</strong>
                  <p className="esm-file__meta">
                    {formatBytes(f.byte_size ?? undefined)} · <StateBadge file={f} />
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="3. Publish">
        <ErrorAlert error={publish.error} title="It was not published" />
        <p>
          Publishing needs at least one file that finished uploading and passed the scan. Until
          then the draft is visible to you alone, and downloads are refused for everyone.
        </p>
        <div className="esm-actions">
          <Button
            busy={publish.isPending}
            disabled={!ready}
            onClick={() =>
              publish.mutate(
                { creationID, publish: true },
                {
                  onSuccess: () =>
                    void navigate({ to: "/creations/$creationID", params: { creationID } }),
                },
              )
            }
          >
            Publish
          </Button>
          <Link to="/creations/$creationID" params={{ creationID }}>
            Open the draft
          </Link>
        </div>
      </Card>
    </>
  );
}
